import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles, productImageStyles } from "@insite/content-library/Components/ProductImage";

import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductSelectionPartsModel } from "@tCooneyBrothers/ApiModels";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    productSelectionPartsModel: ProductSelectionPartsModel;
    extendedStyles?: SparePartProductStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface SparePartProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPriceWrapper?: InjectableCss;
    dataTableRow?: DataTableRowProps;
    submitButton?: InjectableCss;
    description?: InjectableCss;
    productImageWrapper?: InjectableCss;
}

productImageStyles.linkWrappingImage = {
    css: css`
        background: #fff;
        justify-content: center;
        div {
            width: 100%;
        }
        p[class^="TypographyStyle"] {
            max-height: 150px;
            overflow: hidden;
            font-size: 12px;
        }
    `,
};

export const sparePartProductStyles: SparePartProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
            padding: 5px 15px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                padding: 0;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
            padding: 5px 15px;
            & + button[data-test-selector="addToListLink"] {
                justify-content: flex-end;
                position: absolute;
                bottom: 38px;
                right: 0;
            }
            section {
                legend {
                    font-size: 12px;
                    font-weight: 500;
                    margin-right: 5px;
                    & + div {
                        display: flex;
                    }
                }
                [data-test-selector="productPrice_unitNetPrice"] {
                    color: #2e64b0;
                    font-size: 12px;
                    font-weight: 700;
                }
                [data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: #2e64b0;
                    font-size: 11px;
                    font-weight: 700;
                }
            }
        `,
    },
    submitButton: {
        css: css`
            margin: 10px 0;
            cursor: pointer;
            font-family: inherit;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out 0s;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414 !important;
            width: 100%;
            height: 40px;
            max-width: 250px;
            margin-bottom: 10px;
            > span[class^="TypographyStyle"] {
                color: #141414 !important;
                text-align: center;
                margin: 0 auto;
                &:active {
                    color: #141414 !important;
                }
            }
            &:active,
            &:hover {
                border-color: rgb(33, 74, 42);
            }
            font-size: 15px;
            font-weight: bold;
        `,
    },
    description: {
        css: css`
            white-space: pre-wrap;
            height: auto;
        `,
    },
    productImageWrapper: {
        css: css`
            white-space: pre-wrap;
            height: auto;
            > a {
                width: 50px;
                > div {
                    > p[class^="TypographyStyle"] {
                        width: 50px;
                        height: 50px;
                    }
                    > img {
                        width: 50px;
                        height: 50px;
                    }
                }
            }
        `,
    },
};

const SparePartProduct: React.FC<Props> = ({
    carouselId,
    product,
    updateProductInfo,
    extendedStyles,
    productInfo,
    productSelectionPartsModel,
}) => {
    const [styles] = React.useState(() => mergeToNew(sparePartProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <DataTableRow
                    key={productSelectionPartsModel?.id}
                    {...styles.dataTableRow}
                    data-test-selector="invoiceHistory_invoiceLine"
                >
                    <DataTableCell>{`${productSelectionPartsModel.partNumber}. ${productSelectionPartsModel.chartPartName}`}</DataTableCell>
                    <DataTableCell {...styles.productImageWrapper}>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </DataTableCell>
                    <DataTableCell>{`${product.brand?.name} ${product.manufacturerItem}`}</DataTableCell>
                    <DataTableCell {...styles.description}>{productSelectionPartsModel.chartDescription}</DataTableCell>
                    <DataTableCell>
                        {productContext.productInfo.productDetailPath ? (
                            <Link
                                target="_blank"
                                {...styles.submitButton}
                                href={productContext.productInfo.productDetailPath}
                            >
                                {translate("View Specs & Shop")}
                            </Link>
                        ) : null}
                    </DataTableCell>
                </DataTableRow>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SparePartProduct);
